import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface PwaInstallPrompt {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface CustomNotificationAction {
  action: string;
  title: string;
  icon?: string;
}

export interface CustomNotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: CustomNotificationAction[];
  requireInteraction?: boolean;
  silent?: boolean;
}

export interface BackgroundSyncOptions {
  tag: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private platformId = inject<Object>(PLATFORM_ID);
  private swUpdate = inject(SwUpdate);

  private deferredPrompt: PwaInstallPrompt | null = null;
  private isOnlineSubject = new BehaviorSubject<boolean>(navigator.onLine);
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private installPromptSubject = new BehaviorSubject<boolean>(false);

  public readonly isOnline$ = this.isOnlineSubject.asObservable();
  public readonly updateAvailable$ = this.updateAvailableSubject.asObservable();
  public readonly installPromptAvailable$ = this.installPromptSubject.asObservable();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeNetworkStatus();
      this.initializeServiceWorkerUpdates();
      this.initializeInstallPrompt();
      this.initializePushNotifications();
    }
  }

  /**
   * 初始化網路狀態監控
   */
  private initializeNetworkStatus(): void {
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$).subscribe(isOnline => {
      this.isOnlineSubject.next(isOnline);
      console.log(`Network status: ${isOnline ? 'Online' : 'Offline'}`);
    });
  }

  /**
   * 初始化 Service Worker 更新檢查
   */
  private initializeServiceWorkerUpdates(): void {
    if (this.swUpdate.isEnabled) {
      // 檢查更新
      this.swUpdate.versionUpdates
        .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
        .subscribe(() => {
          this.updateAvailableSubject.next(true);
          console.log('New version available');
        });

      // 定期檢查更新 (每 30 分鐘)
      setInterval(() => {
        this.swUpdate.checkForUpdate().then(() => {
          console.log('Checked for updates');
        });
      }, 30 * 60 * 1000);
    }
  }

  /**
   * 初始化 PWA 安裝提示
   */
  private initializeInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (e: any) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.installPromptSubject.next(true);
      console.log('PWA install prompt available');
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installPromptSubject.next(false);
      console.log('PWA installed');
    });
  }

  /**
   * 初始化推播通知
   */
  private initializePushNotifications(): void {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        console.log('Service Worker ready for push notifications');
      });
    }
  }

  /**
   * 應用更新
   */
  async applyUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      await this.swUpdate.activateUpdate();
      window.location.reload();
    }
  }

  /**
   * 顯示 PWA 安裝提示
   */
  async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }

    await this.deferredPrompt.prompt();
    const choiceResult = await this.deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      console.log('User accepted PWA install');
      this.deferredPrompt = null;
      this.installPromptSubject.next(false);
      return true;
    } else {
      console.log('User dismissed PWA install');
      return false;
    }
  }

  /**
   * 檢查是否為 PWA 模式
   */
  isPwaMode(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  /**
   * 請求通知權限
   */
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'default') {
      return await Notification.requestPermission();
    }

    return Notification.permission;
  }

  /**
   * 顯示本地通知
   */
  async showNotification(options: CustomNotificationOptions): Promise<void> {
    const permission = await this.requestNotificationPermission();

    if (permission === 'granted') {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const notificationOptions: NotificationOptions = {
          body: options.body,
          icon: options.icon || '/icons/icon-192x192.png',
          badge: options.badge || '/icons/icon-96x96.png',
          tag: options.tag,
          data: options.data,
          requireInteraction: options.requireInteraction,
          silent: options.silent
        };

        // Add actions if supported
        if (options.actions && 'actions' in Notification.prototype) {
          (notificationOptions as any).actions = options.actions;
        }

        await registration.showNotification(options.title, notificationOptions);
      } else {
        new Notification(options.title, options);
      }
    }
  }

  /**
   * 註冊推播通知
   */
  async subscribeToPushNotifications(vapidPublicKey: string): Promise<PushSubscription | null> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const uint8Array = this.urlBase64ToUint8Array(vapidPublicKey);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: uint8Array.buffer.slice(
          uint8Array.byteOffset,
          uint8Array.byteOffset + uint8Array.byteLength
        ) as ArrayBuffer
      });

      console.log('Push subscription successful:', subscription);
      return subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  /**
   * 取消推播通知訂閱
   */
  async unsubscribeFromPushNotifications(): Promise<boolean> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        console.log('Push subscription cancelled');
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  /**
   * 註冊背景同步
   */
  async registerBackgroundSync(options: BackgroundSyncOptions): Promise<void> {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register(options.tag);

        // 儲存同步資料到 IndexedDB 或 localStorage
        if (options.data) {
          localStorage.setItem(`sync-${options.tag}`, JSON.stringify(options.data));
        }

        console.log(`Background sync registered: ${options.tag}`);
      } catch (error) {
        console.error('Failed to register background sync:', error);
      }
    }
  }

  /**
   * 獲取快取大小
   */
  async getCacheSize(): Promise<number> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    }
    return 0;
  }

  /**
   * 清除快取
   */
  async clearCache(): Promise<void> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('All caches cleared');
    }
  }

  /**
   * 檢查快取狀態
   */
  async getCacheStatus(): Promise<{ name: string; size: number }[]> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      const cacheStatus = await Promise.all(
        cacheNames.map(async (name) => {
          const cache = await caches.open(name);
          const keys = await cache.keys();
          return { name, size: keys.length };
        })
      );
      return cacheStatus;
    }
    return [];
  }

  /**
   * 轉換 VAPID 金鑰格式
   */
  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * 獲取網路資訊
   */
  getNetworkInformation(): any {
    return (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  }

  /**
   * 檢查是否為慢速網路
   */
  isSlowNetwork(): boolean {
    const connection = this.getNetworkInformation();
    if (connection) {
      return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
    }
    return false;
  }

  /**
   * 預載入關鍵資源
   */
  async preloadCriticalResources(urls: string[]): Promise<void> {
    if ('caches' in window) {
      const cache = await caches.open('critical-resources');
      await Promise.all(
        urls.map(url => cache.add(url).catch(err => console.warn(`Failed to preload ${url}:`, err)))
      );
    }
  }
}
